<template>
  <b-form>
    <b-form-row>
      <h3 class="mx-3">
        <template v-if="!inspection || !inspection.id || !inspection.name">New Inventory Inspection</template>
        <template v-else>{{ inspection.name }}</template>
      </h3>
      <toggle-button v-if="this.inspection"
        :value="isActive"
        @change="restartInspection()"
        :sync="true" :disabled="!isSuperAdmin"
        :labels="{ checked: 'Running', unchecked: 'Completed' }"
        :height="26"
        :width="80"
        class="align-middle mt-1"
        :color="{ checked: '#2bd879', unchecked: '#F1516C' }">
      </toggle-button>
    </b-form-row>
    <b-card class="my-4 w-100" title="General Settings">
      <b-form-row>
        <b-col lg="8">
          <b-form-group label="Inspection Name">
            <b-form-input v-model="inspection.name" />
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col md="12">
          <b-form-group
            label="Bidder Restrictions (Optional)"
            description="Restrict inspection filter to selected DSP(s), open to all DSPs by default">
            <mulselect
              :disabled="!isSuperAdmin"
              variant="light-muted"
              :options="bidderOptions"
              option-value-field="company_id"
              option-label-field="name"
              :values="inspection.bidderIds">
            </mulselect>
          </b-form-group>
        </b-col>
      </b-form-row>
    </b-card>

    <b-alert class="mt-5" show>
      The targeting sections are based on the json path of the filter values in the request
      or response, and as such requires special knowledge to operate. Please ask for help!
    </b-alert>

    <b-card class="my-4 w-100" title="Request Targeting Criteria (Required)">
      <b-alert :show="!Object.keys(inspection.reqCriteria).length">
        No request criteria filters created, required!
      </b-alert>

      <b-form-row v-for="key in Object.keys(inspection.reqCriteria)" :key="key">
        <b-col lg="4">
          <b-form-group label="Filter">
            <b-input-group>
              <b-input-group-text slot="prepend">
                JSON Path
              </b-input-group-text>
              <b-form-input disabled
                :value="key">
              </b-form-input>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col lg="7">
            <b-form-group label="Matching values">
              <string-list-editor
                :disabled="!isSuperAdmin"
                :values="inspection.reqCriteria[key]" placeholder="Enter values"></string-list-editor>
            </b-form-group>
        </b-col>
        <b-col>
          <b-form-group class="mt-4 pt-1" v-if="isSuperAdmin">
            <b-btn @click="delRqProp(key)" variant="outline-danger" class="px-3">x</b-btn>
          </b-form-group>
        </b-col>
      </b-form-row>

      <h4 class="mt-5">Add New Request Filter Property</h4>
      <b-form-row>
        <b-form-group label="Property" description="JSON path to property in bid request">
          <b-input-group>
            <b-input-group-text slot="prepend">
              JSON Path
            </b-input-group-text>
            <b-form-input :disabled="!isSuperAdmin" v-model="tmpRqPath"></b-form-input>
          </b-input-group>
        </b-form-group>
      </b-form-row>
      <b-btn :disabled="!isSuperAdmin || !tmpRqPath" variant="secondary" size="sm" @click="addRqProp()">Add</b-btn>
    </b-card>



    <b-card class="mt-5 w-100" title="Response Targeting Criteria (Optional)">
      <b-alert :show="!Object.keys(inspection.resCriteria).length">
        No response criteria filters created
      </b-alert>
      <b-form-row class="my-4" v-for="key in Object.keys(inspection.resCriteria)" :key="key">
        <b-col lg="4">
          <b-form-group label="Filter">
            <b-input-group>
              <b-input-group-text slot="prepend">
                JSON Path
              </b-input-group-text>
              <b-form-input disabled
                            :value="key">
              </b-form-input>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col lg="7">
          <b-form-group label="Matching values">
            <string-list-editor
              :disabled="!isSuperAdmin"
              :values="inspection.resCriteria[key]" placeholder="Enter values"></string-list-editor>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group class="mt-4 pt-1" v-if="isSuperAdmin">
            <b-btn @click="delRsProp(key)" variant="outline-danger" class="px-3">x</b-btn>
          </b-form-group>
        </b-col>
      </b-form-row>

      <h4 class="mt-5">Add New Response Filter Property</h4>
      <b-form-row>
        <b-form-group label="Property" description="JSON path to property in bid response">
          <b-input-group>
            <b-input-group-text slot="prepend">
              JSON Path
            </b-input-group-text>
            <b-form-input :disabled="!isSuperAdmin" v-model="tmpRsPath"></b-form-input>
          </b-input-group>
        </b-form-group>
      </b-form-row>
      <b-btn :disabled="!isSuperAdmin || !tmpRsPath" variant="secondary" size="sm" @click="addRsProp()">Add</b-btn>
    </b-card>

    <b-btn :disabled="!isSuperAdmin" class="mt-4" @click="setInspection()" variant="primary">
      {{ id ? 'Save Changes' : 'Create'}}
    </b-btn>
  </b-form>
</template>

<script>
import auth from "../../auth";
import Mulselect from '@/components/Mulselect'
import StringListEditor from "@/components/StringListEditor"

import {AdminApi} from "../api";
import {DemandApi} from "../../demand/api";

export default {
  name: 'InspectionSettings',
  props: {
    id: { required: false }
  },
  created () {
    if (this.id) {
      AdminApi.inspection(this.id).then((inspection) => {
        if (!inspection.resCriteria)
          inspection.resCriteria = {};
        this.inspection = inspection;
      }).catch((err) => {
        this.$notify.error("Failed to load inspection details")
      })
    }

    this.getBidders()
  },
  data () {
    return {
      isSuperAdmin: auth.isSuperAdmin(),
      tmpRqPath: null,
      tmpRsPath: null,
      bidderOptions: [],
      inspection: {
        completed: false,
        name: null,
        reqCriteria: {},
        resCriteria: {},
        bidderIds: []
      }
    }
  },
  computed: {
    isActive () {
      return !this.inspection.completed && Date.now() <= this.inspection.endTime;
    }
  },
  methods: {
    restartInspection() {
      this.inspection.endTime = Date.now() + 15 * 60 * 1000;
      this.inspection.completed = false;

      this.setInspection()
    },
    addRqProp() {
      const v = this.tmpRqPath

      if (!v ||  !v.length)
        return;

      if (this.inspection.reqCriteria[v] != null)
        return this.$notify.error('Property already exists!')

      this.inspection.reqCriteria[v] = []
      this.tmpRqPath = null
    },
    addRsProp() {
      const v = this.tmpRsPath

      if (!v || !v.length)
        return;

      if (this.inspection.resCriteria[v] != null)
        return this.$notify.error('Property already exists!')

      this.inspection.resCriteria[v] = []
      this.tmpRsPath = null
    },
    delRqProp(prop) {
      this.$delete(this.inspection.reqCriteria, prop)
    },
    delRsProp(prop) {
      this.$delete(this.inspection.resCriteria, prop)
    },
    getBidders () {
      return DemandApi.list().then(list => {
        if (list && list.list)
          this.bidderOptions = list.list
        else
          this.$notify.error("Failed listing bidders")
      })
    },
    setInspection () {
      const ins = this.inspection;
      if (!ins.endTime)
        ins.endTime = Date.now() + 15 * 60 * 1000; // run for 30 mins max

      if (Object.keys(ins.reqCriteria).length == 0)
        return this.$notify.error('Inspection must have request targeting criteria')

      for (const val of Object.values(ins.reqCriteria)) {
        if (val == null || val.length == 0)
          return this.$notify.error('Every request filter must have at least one filter value')
      }

      for (const val of Object.values(ins.resCriteria)) {
        if (val == null || val.length == 0)
          return this.$notify.error('Every response filter must have at least one filter value')
      }

      AdminApi.inspectionsSet({ inspection: ins }).then(response => {
        console.log(response)
        this.$router.go(-1)
      }).catch(error => {
        this.$notify.error('Inspection save failed: ' + error.message)
      })
    }
  },
  components: {
    Mulselect,
    StringListEditor
  }
}
</script>



// WEBPACK FOOTER //
// src/admin/pages/InspectionSettings.vue